import { ReactNode, PropsWithChildren } from "react";

import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip, TooltipProps, Typography } from "antd";
import { clsx } from "clsx";

import { RVFormFloatingLabel } from "../../atoms/RVFormFloatingLabel/RVFormFloatingLabel";
import { RVFormItem, RVFormItemProps } from "../../atoms/RVFormItem/RVFormItem";

import styles from "./RVFormFloatingLabelWrap.module.scss";

/**
 * Props for the RVFormFloatingLabelWrap component.
 * Extends RVFormItemProps with additional floating label functionality.
 */
export type RVFormFloatingLabelWrapProps = RVFormItemProps & {
    /** Allows to override the automatic padding of form items */
    noPadding?: boolean;
    /** Whether to show the form label above the field */
    showFormLabel?: boolean;
    /** Custom header content for the form item */
    formItemHeader?: ReactNode;
    /** Tooltip content to show next to the form header */
    formItemHeaderTooltip?: ReactNode;
    /** Tooltip placement for the form header */
    formItemHeaderTooltipPlacement?: TooltipProps["placement"];
    /** Whether the field is disabled */
    disabled?: boolean;
    /** CSS class name for the container */
    containerClassName?: string;
    /** Whether the floating label should remain fixed in the floating position */
    fixedFloatingLabel?: boolean;
    /** Whether the field is clearable (affects floating label behavior) */
    clearable?: boolean;
    /** Placeholder text for the field */
    placeholder?: string;
    /** Info text to display below the label */
    infoText?: string | ReactNode;
    /** Sub info text to display below the field */
    subInfoText?: string;
    /** Data test ID for testing purposes */
    dataTestId?: string;
    /** Whether to disable the placeholder functionality */
    disablePlaceholder?: boolean;
    /** Suffix text to display after the field */
    suffix?: string;
    /** CSS class name for the suffix container */
    suffixClassName?: string;
};

/**
 * Internal component that conditionally wraps children with a suffix.
 *
 * @param props - Component props
 * @param props.children - React children to wrap
 * @param props.suffix - Optional suffix text to display
 * @param props.className - Optional CSS class name for the suffix container
 * @returns JSX.Element - The wrapped component or children as-is
 */
const WithSuffix = ({ children, suffix, className }: { children: ReactNode; suffix?: string; className?: string }) => {
    if (!suffix) {
        return <>{children}</>;
    }

    return (
        <div className={clsx(styles.withSuffix, className)}>
            {children}
            {suffix && <Typography.Text className={styles.suffixText}>{suffix}</Typography.Text>}
        </div>
    );
};

/**
 * A comprehensive form field wrapper that combines floating labels with enhanced form functionality.
 *
 * This component wraps Ant Design Form.Item with floating label behavior, additional info text,
 * tooltips, suffixes, and other UI enhancements. It provides a consistent and polished form
 * field experience across the application.
 *
 * Key features:
 * - Floating label animation based on field focus and value state
 * - Optional form labels with required indicators and tooltips
 * - Info text and sub-info text for additional context
 * - Suffix support for units or additional text
 * - Automatic test ID generation
 * - Full integration with Ant Design form validation
 * - Disabled state styling
 *
 * @param props - RVFormFloatingLabelWrap component props
 * @param props.name - Form field name (required for form integration)
 * @param props.label - Field label text
 * @param props.placeholder - Placeholder text for the field
 * @param props.showFormLabel - Whether to show the form label above the field
 * @param props.formItemHeader - Custom header content (overrides label)
 * @param props.formItemHeaderTooltip - Tooltip content for the header
 * @param props.fixedFloatingLabel - Whether the floating label should stay fixed
 * @param props.clearable - Whether the field is clearable (affects floating behavior)
 * @param props.infoText - Info text to display below the label
 * @param props.subInfoText - Sub info text to display below the field
 * @param props.suffix - Suffix text to display after the field (e.g., "USD", "years")
 * @param props.disabled - Whether the field is disabled
 * @param props.className - CSS class for the Form.Item
 * @param props.containerClassName - CSS class for the outer container
 * @param props.suffixClassName - CSS class for the suffix container
 * @param props.dataTestId - Custom test ID (defaults to enhanced-input-{name})
 * @param props.disablePlaceholder - Whether to disable placeholder functionality
 * @param props.children - Form input component (Input, Select, etc.)
 * @param props.rules - Ant Design validation rules
 * @returns JSX.Element - The wrapped form field component
 */
export function RVFormFloatingLabelWrap({
    name,
    noPadding = false,
    showFormLabel,
    formItemHeader,
    formItemHeaderTooltip,
    formItemHeaderTooltipPlacement = "topLeft",
    label,
    fixedFloatingLabel,
    className,
    containerClassName,
    suffixClassName,
    clearable,
    children,
    placeholder,
    infoText,
    subInfoText,
    dataTestId,
    disablePlaceholder,
    suffix,
    ...rest
}: PropsWithChildren<RVFormFloatingLabelWrapProps>): JSX.Element {
    const showRequiredLabel =
        rest.rules?.some(
            // @ts-expect-error type is wrong this is a BaseRule from antd
            (rule) => rule.required === true || typeof rule.validator === "function"
        ) || rest.required;

    return (
        <div
            data-testid={dataTestId ?? `enhanced-input-${name}`}
            className={clsx(
                styles.formItemContainer,
                "floating-design",
                {
                    "floating-design-disabled": rest.disabled,
                    [styles.isFullPageForm]: rest.isFullPageForm,
                    [styles.noPadding]: noPadding,
                },
                containerClassName
            )}
        >
            {showFormLabel && (
                <div className="ant-form-item-label">
                    <label>
                        {formItemHeader ?? label} {showRequiredLabel && <sup>*</sup>}
                        {formItemHeaderTooltip ? (
                            <Tooltip title={formItemHeaderTooltip} placement={formItemHeaderTooltipPlacement}>
                                <InfoCircleOutlined className={styles.tooltipIcon} />
                            </Tooltip>
                        ) : (
                            ""
                        )}
                    </label>
                </div>
            )}
            {infoText && <Typography.Text className="info-text">{infoText}</Typography.Text>}
            <WithSuffix suffix={suffix} className={suffixClassName}>
                <RVFormFloatingLabel name={name} fixedFloatingLabel={fixedFloatingLabel} clearable={clearable}>
                    <RVFormItem
                        data-testid={`${name}-testid`}
                        label={disablePlaceholder ? "" : placeholder || label}
                        className={className}
                        {...rest}
                        name={name}
                        tooltip={undefined}
                        required={false}
                    >
                        {children}
                    </RVFormItem>
                </RVFormFloatingLabel>
            </WithSuffix>
            {subInfoText && <div className={styles.formItemSubText}>{subInfoText}</div>}
        </div>
    );
}
